"use client";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { FormFunnelRichText } from "@portal/app/_components/formFunnel/FormFunnelRichText";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";
import { SANS_FONT_STACK } from "@portal/app/_components/theme/options/typography";
import { PAGE_BACKGROUND, PAGE_DESKTOP_MEDIA } from "../constants";
import type { ResolvedPageContentBlock } from "../types";

const headingSizes = {
  small: { mobile: 28, desktop: 36 },
  medium: { mobile: 36, desktop: 48 },
  large: { mobile: 40, desktop: 60 },
};
const statIcons = { wage: "$", growth: "↗", openings: "+" };

/** Only type-specific content; the renderer owns block placement and surfaces. */
export function LandingPageBlock({ block }: { block: ResolvedPageContentBlock }) {
  const color = block.background === "brand" ? "common.white" : PAGE_BACKGROUND.brand;
  const bodyColor = block.background === "brand" ? "common.white" : "text.primary";

  switch (block.blockType) {
    case "heading": {
      const { text, eyebrow, description } = block.resolvedContent;
      const size = headingSizes[block.size];
      return (
        <Box sx={{ textAlign: block.align }}>
          {eyebrow && (
            <Typography
              sx={{ color, fontSize: 14, fontWeight: 700, letterSpacing: ".06em", mb: 2 }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            component={`h${block.level}`}
            sx={{
              color,
              fontFamily: SANS_FONT_STACK,
              fontWeight: 800,
              fontSize: size.mobile,
              lineHeight: 1.1,
              m: 0,
              [PAGE_DESKTOP_MEDIA]: { fontSize: size.desktop },
            }}
          >
            {text}
          </Typography>
          {description && (
            <Typography
              sx={{ color: bodyColor, mt: 2, mb: 0, fontSize: { xs: 18, md: 20 }, lineHeight: 1.5 }}
            >
              {description}
            </Typography>
          )}
        </Box>
      );
    }
    case "richText":
      return (
        <Box
          sx={{
            color: bodyColor,
            fontSize: block.size === "small" ? 14 : 18,
            textAlign: block.align,
            // The shared article renderer sets its own alignment and body size.
            "&& > div": { textAlign: block.align },
            "&& :is(h1, h2, h3, h4, h5, h6, p, ul, ol, li, blockquote)": { textAlign: block.align },
            "&& p, && li": { fontSize: block.size === "small" ? 14 : 18 },
            "& p:last-child": { mb: 0 },
            ...(block.background === "brand" && {
              "& > div": { color: bodyColor },
              "&& :is(h1, h2, h3, h4, h5, h6, p, li, a, blockquote, strong, em)": {
                color: bodyColor,
              },
            }),
          }}
        >
          <FormFunnelRichText content={block.resolvedContent.content} appearance="article" />
        </Box>
      );
    case "image": {
      const { image, caption } = block.resolvedContent;
      const fillsFrame = block.sizing !== "natural";
      const tallFrame = block.sizing === "tall";
      const overlayCaption = block.captionPlacement === "overlay";
      return (
        <Box
          component="figure"
          sx={{
            m: 0,
            height: block.sizing === "fill" ? "100%" : "auto",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Box
            data-testid="landing-page-image-frame"
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: block.radius === "rounded" ? "20px" : 0,
              flex: tallFrame ? "none" : 1,
              height: tallFrame ? "clamp(320px, 70svh, 760px)" : undefined,
              minHeight: block.sizing === "fill" ? 320 : undefined,
              aspectRatio:
                block.sizing === "landscape"
                  ? "16/9"
                  : block.sizing === "portrait"
                    ? "2/3"
                    : undefined,
              [PAGE_DESKTOP_MEDIA]: {
                minHeight: block.sizing === "fill" ? 420 : undefined,
                height: tallFrame ? "clamp(420px, calc(100svh - 160px), 760px)" : undefined,
              },
            }}
          >
            <img
              src={image.url}
              alt={image.alt}
              width={image.width}
              height={image.height}
              style={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                height: fillsFrame ? "100%" : "auto",
                position: fillsFrame ? "absolute" : "relative",
                inset: fillsFrame ? 0 : undefined,
                objectFit: block.fit,
                objectPosition: block.focus,
              }}
            />
          </Box>
          {caption && (
            <Typography
              component="figcaption"
              sx={
                overlayCaption
                  ? {
                      position: "absolute",
                      bottom: "16px",
                      left: "16px",
                      maxWidth: "calc(100% - 32px)",
                      maxHeight: "calc(100% - 32px)",
                      overflowY: "auto",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      px: "16px",
                      py: "12px",
                      borderRadius: "20px",
                      bgcolor: "common.white",
                      color: PAGE_BACKGROUND.brand,
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.4,
                      m: 0,
                    }
                  : { color: bodyColor, mt: 1, fontSize: 13 }
              }
            >
              {overlayCaption ? (
                <>
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "currentColor",
                      flexShrink: 0,
                      mt: "5px",
                    }}
                  />
                  <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    {caption}
                  </Box>
                </>
              ) : (
                caption
              )}
            </Typography>
          )}
        </Box>
      );
    }
    case "stats": {
      const { eyebrow, title, items, sourceText, sourceUrl, sourceLinkLabel } =
        block.resolvedContent;
      const iconLeft = block.itemLayout === "icon-left";
      return (
        <Box>
          {eyebrow && (
            <Typography
              sx={{
                color: bodyColor,
                mb: 1,
                fontSize: 13,
                letterSpacing: ".08em",
                fontWeight: 600,
              }}
            >
              {eyebrow}
            </Typography>
          )}
          {title && (
            <Typography
              component="h2"
              sx={{
                color: bodyColor,
                mb: 2,
                mt: 0,
                fontSize: block.size === "small" ? 22 : 26,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          )}
          <Box
            component="dl"
            sx={{
              m: 0,
              display: "grid",
              gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${iconLeft ? 200 : 160}px), 1fr))`,
              gap: "12px",
            }}
          >
            {items.map((item, index) => {
              const column = iconLeft && item.icon ? 2 : 1;
              const icon = item.icon && (
                <Box
                  component="span"
                  data-stat-part="icon"
                  aria-hidden="true"
                  sx={{
                    fontSize: iconLeft ? 24 : 18,
                    mr: iconLeft ? 0 : 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: iconLeft ? "48px" : "28px",
                    height: iconLeft ? "48px" : "28px",
                    verticalAlign: "middle",
                    bgcolor: PAGE_BACKGROUND.brand,
                    color: "common.white",
                    borderRadius: "8px",
                    ...(iconLeft && {
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }),
                  }}
                >
                  {statIcons[item.icon]}
                </Box>
              );
              return (
                <Box
                  component="div"
                  data-testid="landing-page-stat-item"
                  key={index}
                  sx={{
                    minWidth: 0,
                    p: "16px",
                    borderRadius: "16px",
                    bgcolor: PAGE_BACKGROUND.muted,
                    color: "#2b3242",
                    containerType: "inline-size",
                    ...(iconLeft && {
                      position: "relative",
                      display: "grid",
                      gridTemplateColumns: item.icon ? "48px minmax(0, 1fr)" : "minmax(0, 1fr)",
                      columnGap: "12px",
                      alignContent: "center",
                      minHeight: "80px",
                    }),
                  }}
                >
                  <Typography
                    component="dt"
                    data-stat-part="label"
                    sx={{
                      color: "#2b3242",
                      fontSize: 14,
                      lineHeight: 1.4,
                      ...(iconLeft && { gridColumn: column, gridRow: 2, mt: "4px" }),
                    }}
                  >
                    {iconLeft && icon}
                    {item.label}
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{
                      m: 0,
                      mt: iconLeft ? 0 : 1,
                      color: PAGE_BACKGROUND.brand,
                      fontSize: iconLeft ? undefined : "clamp(22px, 16cqi, 36px)",
                      lineHeight: 1.15,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      overflowWrap: "anywhere",
                      ...(iconLeft && {
                        gridColumn: column,
                        gridRow: 1,
                        minWidth: 0,
                        containerType: "inline-size",
                      }),
                    }}
                  >
                    {!iconLeft && icon}
                    <Box
                      component="span"
                      data-stat-part="value"
                      sx={
                        iconLeft
                          ? { display: "block", fontSize: "clamp(20px, 21cqi, 36px)" }
                          : undefined
                      }
                    >
                      {String(item.value)}
                    </Box>
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {(sourceText || sourceUrl) && (
            <Typography sx={{ color: bodyColor, fontSize: 12, lineHeight: 1.5, mt: 2 }}>
              {sourceText}{" "}
              {sourceUrl && isSafeFormFunnelContentHref(sourceUrl) && (
                <Link
                  href={sourceUrl}
                  sx={{ color: block.background === "brand" ? "common.white" : "primary.main" }}
                >
                  {sourceLinkLabel || "Source"}
                </Link>
              )}
            </Typography>
          )}
        </Box>
      );
    }
    case "testimonial": {
      const { quote, name, photo } = block.resolvedContent;
      return (
        <Box component="figure" sx={{ m: 0, textAlign: block.align }}>
          <Typography
            component="blockquote"
            sx={{ color: bodyColor, m: 0, fontSize: 20, lineHeight: 1.5 }}
          >
            &ldquo;{quote}&rdquo;
          </Typography>
          <Box
            component="figcaption"
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: block.align === "center" ? "center" : "flex-start",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {photo && (
              <Box
                component="img"
                src={photo.url}
                alt={photo.alt}
                width={48}
                height={48}
                sx={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
              />
            )}
            <Typography sx={{ color: bodyColor, fontWeight: 600 }}>{name}</Typography>
          </Box>
        </Box>
      );
    }
  }
}
